import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { IAtp } from 'app/models/atp/atp';
import { IAtpAccreditation } from 'app/models/atp/atp-accreditation';
import { IAtpAddress } from 'app/models/atp/atp-address';
import { IAtpApplication } from 'app/models/atp/atp-application';
import { IAtpApplicationListing } from 'app/models/atp/atp-application-listing';
import { IAtpContactInformation } from 'app/models/atp/atp-contact-information';
import { IAtpCourse } from 'app/models/atp/atp-course';
import { IAtpCourseAddress } from 'app/models/atp/atp-course-address';
import { IAtpCourseDocument } from 'app/models/atp/atp-course-document';
import { IAtpCourseStudent } from 'app/models/atp/atp-course-student';
import { IAtpDashboard } from 'app/models/atp/atp-dashboard';
import { IAtpLink } from 'app/models/atp/atp-link';
import { IAtpPerson } from 'app/models/atp/atp-person';
import { IAtpFilter } from 'app/models/filters/atp-filter';
import { ICourseFilter } from 'app/models/filters/course-filter';
import { Observable } from 'rxjs';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
  }),
  withCredentials: true
};

const httpOptionsFormData = {
  headers: new HttpHeaders({
    'Cache-Control': 'no-cache',
  }),
  withCredentials: true
};

@Injectable({
  providedIn: 'root'
})

export class AtpService {
  private api: string = environment.privateApi;
  actionType: Array<String>[] = [];
  errorMessages = [];

  constructor(private http?: HttpClient) {}

  deleteAtpApplication(id: number): Observable<any> {    
    return this.http.get(this.api + 'Atp/DeleteATPApplication/' + id, httpOptions);
  }

  saveAtpApplication(application: IAtpApplication): Observable<any> { 
    return this.http.post(this.api + 'Atp/SaveATPApplication', application, httpOptions);
  }

  getAtpApplication(id:number): Observable<IAtpApplication> {    
    return this.http.get<IAtpApplication>(this.api + 'Atp/GetATPApplicationById/' + id, { withCredentials: true });
  }

  getAtpPublicDashboardData(): Observable<IAtpDashboard[]> {
    return this.http.get<IAtpDashboard[]>(this.api + 'Atp/GetATPPublicDashboardData', { withCredentials: true });
  }

  getAtpForLinking(id: string, atpId?: number): Observable<IAtpLink> {
    let params = new HttpParams();

    if (id !== null && id !== undefined) {
      params = params.set('id', id);
    }

    if (atpId !== null && atpId !== undefined) {
      params = params.set('atpId', atpId.toString());
    }

    return this.http.get<IAtpLink>(this.api + 'Atp/GetATPForLinking', { params, withCredentials: true });
  }

  getAtpById(id: number): Observable<IAtpLink> {
    return this.http.get<IAtpLink>(this.api + 'Atp/GetATPById/' + id, { withCredentials: true });
  }

  getAtpFilteredCourses(filter: ICourseFilter): Observable<IAtpCourse[]> {
    let params = new HttpParams();
    for (const key in filter) {
      if (filter.hasOwnProperty(key) && filter[key] !== null && filter[key] !== undefined) {
        params = params.append(key, filter[key]);
      }
    }   
    return this.http.get<IAtpCourse[]>(environment.privateApi + 'Atp/GetATPFilteredCourses', { params, withCredentials: true });
  }

  getAtpAccreditations(atpId:number): Observable<IAtpAccreditation[]> {    
    return this.http.get<IAtpAccreditation[]>(this.api + 'Atp/GetATPAccreditations/' + atpId, { withCredentials: true });
  }

  getAtpAddresses(id:number): Observable<IAtpAddress[]> {    
    return this.http.get<IAtpAddress[]>(this.api + 'Atp/GetATPAddresses/' + id, { withCredentials: true });
  }

  getAtpContactInformation(id:number): Observable<IAtpContactInformation> {    
    return this.http.get<IAtpContactInformation>(this.api + 'Atp/GetATPContactInformation/' + id, { withCredentials: true });
  }

  getATPPeople(atpId:number): Observable<IAtpPerson[]> {    
    return this.http.get<IAtpPerson[]>(this.api + 'Atp/GetATPPeople/' + atpId, { withCredentials: true });
  }

  getAtpUsers(id: number): Observable<any> {
    return this.http.get<any>(this.api + 'Atp/GetAtpUsers/' + id, { withCredentials: true });
  }

  getATPCourseById(id: number): Observable<IAtpCourse> {    
    return this.http.get<IAtpCourse>(this.api + 'Atp/GetATPCourseById/' + id, { withCredentials: true });
  }

  getATPCourseAddresses(atpId:number): Observable<IAtpCourseAddress[]> {    
    return this.http.get<IAtpCourseAddress[]>(this.api + 'Atp/GetATPCourseAddresses/' + atpId, { withCredentials: true });
  }

  getATPCourseAddressesByAtpId(atpId:number): Observable<IAtpCourseAddress[]> {
    return this.http.get<IAtpCourseAddress[]>(this.api + 'Atp/GetATPCourseAddressesByAtpId/' + atpId, { withCredentials: true });
  }

  saveATPCourse(atpCourse: IAtpCourse): Observable<any> { 
    return this.http.post(this.api + 'Atp/SaveATPCourse', atpCourse, httpOptions);
  }

  saveATPCourseAddress(atpCourse: IAtpCourseAddress): Observable<any> { 
    return this.http.post(this.api + 'Atp/SaveATPCourseAddress', atpCourse, httpOptions);
  }

  deleteATPCourseStudent(id: number): Observable<any> {    
    return this.http.get(this.api + 'Atp/DeleteATPCourseStudent/' + id, httpOptions);
  }

  getATPCourseStudents(courseId:number): Observable<IAtpCourseStudent[]> {    
    return this.http.get<IAtpCourseStudent[]>(this.api + 'Atp/GetATPCourseStudents/' + courseId, { withCredentials: true });
  }

  getATPCourseStudentById(studentId:number): Observable<IAtpCourseStudent> {        
    return this.http.get<IAtpCourseStudent>(this.api + 'Atp/GetATPCourseStudentById/' + studentId, { withCredentials: true });
  }

  saveATPCourseStudent(atpCourseStudent: IAtpCourseStudent): Observable<any> { 
    return this.http.post(this.api + 'Atp/SaveATPCourseStudent', atpCourseStudent, httpOptions);
  }

  saveATP(atp: IAtp): Observable<any> {
    return this.http.post(environment.privateApi + 'Atp/SaveATP', atp, httpOptions);
  }

  saveATPAddress(address: IAtpAddress): Observable<any> {
    return this.http.post<any>(environment.privateApi + 'Atp/SaveATPAddress', address, httpOptions);
  }

  saveATPContactInformation(contactInformation: IAtpContactInformation): Observable<any> { 
    return this.http.post(this.api + 'Atp/SaveATPContactInformation', contactInformation, httpOptions);
  }

  saveATPPerson(atpPerson: IAtpPerson): Observable<any> { 
    return this.http.post(this.api + 'Atp/SaveATPPerson', atpPerson, httpOptions);
  }

  deleteATPPerson(id: number): Observable<any> {
    return this.http.get(this.api + 'Atp/DeleteATPPerson/' + id, httpOptions);
  }

  requestATPStaffAffiliation(atpId: number, personId: number): Observable<any> {
    return this.http.post(this.api + 'Atp/RequestATPStaffAffiliation', {
      personId,
      entityType: 'ATP',
      entityId: atpId,
    }, httpOptions);
  }

  cancelATPStaffAffiliationRequest(atpId: number, personId: number): Observable<any> {
    return this.http.post(this.api + 'Atp/CancelATPStaffAffiliationRequest', {
      personId,
      entityType: 'ATP',
      entityId: atpId,
    }, httpOptions);
  }

  getAllUserAtpApplications(): Observable<IAtpApplicationListing[]> {        
    return this.http.get<IAtpApplicationListing[]>(this.api + 'Atp/GetAllUserAtpApplications/', { withCredentials: true });
  }

  GetAllUserATPs(filter: IAtpFilter): Observable<IAtp[]> {
    let params = new HttpParams();
    for (const key in filter) {
      if (filter.hasOwnProperty(key) && filter[key] !== null && filter[key] !== undefined) {
        params = params.append(key, filter[key]);
      }
    }    
    return this.http.get<IAtp[]>(environment.privateApi + 'Atp/GetAllUserATPs', { params, withCredentials: true });
  }

  getAllAtpAccreditationApplications(atpId: number): Observable<IAtpApplicationListing[]> {        
    return this.http.get<IAtpApplicationListing[]>(this.api + 'Atp/GetAllAtpAccreditationApplications/' + atpId, { withCredentials: true });
  }

  getAllAtpNamesForUser(): Observable<IAtp[]> {  
    return this.http.get<IAtp[]>(environment.privateApi + 'Atp/GetAllAtpNamesForUser', { withCredentials: true });
  }

  getAllAtpNames(): Observable<IAtp[]> {  
    return this.http.get<IAtp[]>(environment.privateApi + 'Atp/GetAllAtpNames', { withCredentials: true });
  }

  deleteATPPendingCourseStudent(id: number): Observable<any> {    
    return this.http.get(this.api + 'Atp/DeleteATPPendingCourseStudent/' + id, httpOptions);
  }

  getATPPendingCourseStudents(courseId:number): Observable<IAtpCourseStudent[]> {    
    return this.http.get<IAtpCourseStudent[]>(this.api + 'Atp/GetATPPendingCourseStudents/' + courseId, { withCredentials: true });
  }

  getATPPendingCourseStudentById(studentId:number): Observable<IAtpCourseStudent> {        
    return this.http.get<IAtpCourseStudent>(this.api + 'Atp/GetATPPendingCourseStudentById/' + studentId, { withCredentials: true });
  }

  saveATPPendingCourseStudent(atpCourseStudent: IAtpCourseStudent): Observable<any> { 
    return this.http.post(this.api + 'Atp/SaveATPPendingCourseStudent', atpCourseStudent, httpOptions);
  }

  // ATP Course Document functions
  getATPCourseDocuments(courseId: number): Observable<IAtpCourseDocument[]> {
    return this.http.get<IAtpCourseDocument[]>(this.api + 'Atp/GetATPCourseDocuments/' + courseId, { withCredentials: true });
  }

  saveATPCourseDocument(file: File, courseId: number, documentType: string): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('courseId', courseId.toString());
    formData.append('documentType', documentType);
    return this.http.post(this.api + 'Atp/SaveATPCourseDocument', formData, httpOptionsFormData);
  }

  deleteATPCourseDocument(id: number): Observable<any> {
    return this.http.delete(this.api + 'Atp/DeleteATPCourseDocument/' + id, { withCredentials: true });
  }

  getATPExampleDocuments(): Observable<any[]> {
    return this.http.get<any[]>(this.api + 'Atp/GetATPExampleDocuments', { withCredentials: true });
  }

  downloadATPCourseDocument(fileName: string): Observable<Blob> {
    return this.http.get(this.api + 'Document/DownloadBlob/' + encodeURIComponent(fileName), {
      withCredentials: true,
      responseType: 'blob'
    });
  }
}